import IosShareIcon from '@mui/icons-material/IosShare'
import Grid from '@mui/material/Grid'
import IconButton from '@mui/material/IconButton'
import MenuItem from '@mui/material/MenuItem'
import { styled } from '@mui/material/styles'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import Toolbar from '@mui/material/Toolbar'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import Box from '@mui/system/Box'
import Container from '@mui/system/Container'
import {
  HELP_ME_BODY,
  HELP_ME_RECIPIENT,
  HELP_ME_SUBJECT,
} from 'src/constants/modal.constants'
import {
  CORPORATE_CREATE_PLAN_TABS,
  CORPORATE_VIEW_PLAN_TABS,
  DIVISION_VIEW_PLAN_TABS,
  MULTI_FAMILY_TABS,
  PLANS_TABS,
  VIEW_ONLY_FAVORITES_TABS,
  VIEW_ONLY_MULTI_FAMILY_TABS,
  VIEW_ONLY_SINGLE_FAMILY_TABS,
} from 'src/constants/plans.constants'
import { lennarTheme } from 'src/lennarTheme'
import {
  classTypes,
  familyTypes,
  fieldNames,
  PDF_EXPORT,
  PLAN_CONTAINER_LABEL,
  planCategories,
  SearchAndCompareEnum,
  shellFieldNames,
  structureTypes,
  unitFieldNames,
} from 'src/lib/constants'
import { getEnvVar } from 'src/lib/env'
import { ROUTES } from 'src/lib/routes'
import { formatCurrency } from 'src/lib/utilities'
import { setSelectedFavoritesTab } from 'src/redux/slice/GetFavoritePlansSlice'
import { setSelectedTab } from 'src/redux/slice/MultiFamilyPlanListingSlice'
import { setSinglePlansPageTab } from 'src/redux/slice/planListingSlice'
import {
  ComparisonAttributeItem,
  FilterMenuItems,
} from 'src/redux/slice/SearchCompareData'
import { clearAllPlansActiveElevations } from 'src/redux/slice/selectedPlansSlice'
import { AppDispatch } from 'src/redux/stores/store'
import {
  FilterFieldsOptions,
  FilterValue,
  IPlan,
  IUnit,
} from 'src/types/filterPlans.interfaces'
import { IShell } from 'src/types/filterShells.interfaces'
import {
  CorePlanImageTabType,
  ImageTypeEnum,
} from 'src/types/planImages.interface.d'
import { toDateString } from 'src/utils/conversion.util'
import { getFeet } from 'src/utils/sliderFilter.util'

import {
  foundationType_,
  planStatus_,
  planType_,
  structureType,
} from './PlanEntryForms/PlanMasterData/PlanEntryFieldsData'
import { yesNo } from './Plans/utils/constants'
import {
  CommunityFloorPlanImagesResponse,
  GetCommunityFloorPlanImagesOptions,
} from './ViewOnly/ExportAttributes/exportAttributes.interface'

const {
  lennarBlues: { lennarBlue },
} = lennarTheme.lennarDesignSystem

export const containedButtonSx = {
  background: lennarBlue,
  textTransform: 'capitalize',
  typography: { fontFamily: 'inherit' },
}

export const outlinedButtonSx = {
  color: lennarBlue,
  textTransform: 'capitalize',
  typography: { fontFamily: 'inherit' },
  whiteSpace: 'nowrap',
}

export const StyledTab = styled(Tab)(() => ({}))

export const StyledTabTitle = styled(Tooltip)(({ theme }) => ({
  fontSize: theme.typography.pxToRem(15),
  fontWeight: theme.typography.fontWeightRegular,
  marginRight: theme.spacing(1),
  textTransform: 'none',
}))

const SelectPlaceholder = () => (
  <Box
    component={'span'}
    style={{ color: lennarTheme.lennarDesignSystem.type.placeholder }}
  >
    Select
  </Box>
)

const isViewOnlyEnabled = getEnvVar('REACT_APP_VIEW_ONLY_ENABLED') === 'true'

export const renderValue = (value: string | number | null) => {
  const shouldRender = value || (typeof value === 'string' && value.length)
  return shouldRender ? value : <SelectPlaceholder />
}

export const renderNumberValue = (value: string | number) => {
  const shouldRender = !isNaN(Number(value)) || value === 0 || Number(value)
  return shouldRender ? value : <SelectPlaceholder />
}

export const handleTabChange = (
  _event: React.SyntheticEvent<Element, Event>,
  newValue: number,
  tabsToShow: Record<string, string | undefined>,
  dispatch: AppDispatch,
  navigate: (path: string) => void,
  _userUuid: string | null,
  planFamilyType: string | null,
  isCorporateViewUser: boolean,
  isDivisionViewUser: boolean,
  isFavoritesPage: boolean
) => {
  const tabValue = Object.keys(tabsToShow)[newValue]

  const favoritesTabRoutes: Record<string, string> = {
    [VIEW_ONLY_FAVORITES_TABS.SHELL_PLANS]: ROUTES.FAVORITES_SHELLS,
    [VIEW_ONLY_FAVORITES_TABS.SINGLE_FAMILY_PLANS]:
      ROUTES.FAVORITES_SINGLE_FAMILY,
    [VIEW_ONLY_FAVORITES_TABS.UNIT_PLANS]: ROUTES.FAVORITES_UNITS,
  }

  if (isFavoritesPage) {
    const route = favoritesTabRoutes[tabValue]
    const familyType =
      tabValue === VIEW_ONLY_FAVORITES_TABS.SINGLE_FAMILY_PLANS
        ? familyTypes.SINGLE
        : familyTypes.MULTI
    dispatch(setSelectedFavoritesTab(tabValue))
    navigate(`${route}?plan-type=${familyType}`)
    return
  }
  if (planFamilyType === familyTypes.SINGLE) {
    dispatch(setSinglePlansPageTab(tabValue))
  } else if (planFamilyType === familyTypes.MULTI) {
    dispatch(setSelectedTab(tabValue))
  } else {
    return
  }

  const isViewUser = isCorporateViewUser || isDivisionViewUser
  const searchAndCompareMode = checkSearchAndCompareMode()

  const showSearchAndCompare =
    isViewOnlyEnabled && (isViewUser || searchAndCompareMode)

  const tabRoutes: Record<string, string> = {
    [PLANS_TABS.ALL_PLANS]: ROUTES.PLANS,
    [PLANS_TABS.DIVISION]: ROUTES.DIVISION,
    [PLANS_TABS.DRAFTS]: ROUTES.DRAFTS,
    [PLANS_TABS.FAVORITES]: showSearchAndCompare ? '' : ROUTES.FAVORITES,
    [PLANS_TABS.SHARED_WITH_ME]: showSearchAndCompare
      ? ''
      : ROUTES.SHARED_WITH_ME,
    [VIEW_ONLY_MULTI_FAMILY_TABS.SHELL_PLANS]: ROUTES.SHELL_PLANS,
    [VIEW_ONLY_MULTI_FAMILY_TABS.UNIT_PLANS]: ROUTES.UNIT_PLANS,
  }

  const route = tabRoutes[tabValue]
  if (route) {
    navigate(`${route}?plan-type=${planFamilyType}`)
  }
  dispatch(clearAllPlansActiveElevations())
}

export function getTabsToShow(
  isCorporateCreateUser: boolean,
  isCorporateViewUser: boolean,
  isDivisionCreateUser: boolean,
  isDivisionViewUser: boolean,
  userUuid: string | null,
  planFamilyType: string | null,
  isFavoritesPage: boolean = false
) {
  const filterTabs = (tabs: Record<string, string>, keysToRemove: string[]) => {
    const filteredTabs = { ...tabs }
    keysToRemove.forEach((key) => delete filteredTabs[key])
    return filteredTabs
  }

  const isViewUser = isCorporateViewUser || isDivisionViewUser
  const searchAndCompareMode = checkSearchAndCompareMode()

  const showSearchAndCompare =
    isViewOnlyEnabled && (isViewUser || searchAndCompareMode)

  if (isFavoritesPage) {
    return VIEW_ONLY_FAVORITES_TABS
  }

  if (planFamilyType === familyTypes.SINGLE && showSearchAndCompare) {
    return VIEW_ONLY_SINGLE_FAMILY_TABS
  }

  if (planFamilyType === familyTypes.MULTI) {
    if (showSearchAndCompare) {
      return VIEW_ONLY_MULTI_FAMILY_TABS
    }
    const tabs =
      isDivisionCreateUser || isDivisionViewUser
        ? MULTI_FAMILY_TABS
        : filterTabs(MULTI_FAMILY_TABS, ['DIVISION'])

    return isDivisionViewUser || isCorporateViewUser
      ? filterTabs(tabs, ['DRAFTS'])
      : tabs
  }

  if (isCorporateCreateUser) {
    const baseTabs =
      isCorporateViewUser || isDivisionViewUser
        ? filterTabs(CORPORATE_CREATE_PLAN_TABS, ['DRAFTS'])
        : { ...CORPORATE_CREATE_PLAN_TABS }

    const tabsToShow = !userUuid
      ? filterTabs(baseTabs, ['BULK_EXPORTS'])
      : baseTabs

    return Object.keys(tabsToShow).length > 0
      ? tabsToShow
      : CORPORATE_CREATE_PLAN_TABS
  }

  if (isCorporateViewUser) {
    return !userUuid
      ? filterTabs(CORPORATE_VIEW_PLAN_TABS, ['BULK_EXPORTS'])
      : CORPORATE_VIEW_PLAN_TABS
  }

  if (isDivisionViewUser) {
    const baseTabs = !userUuid
      ? filterTabs(DIVISION_VIEW_PLAN_TABS, ['BULK_EXPORTS'])
      : { ...DIVISION_VIEW_PLAN_TABS }

    const tabsToShow =
      isCorporateViewUser || isCorporateCreateUser
        ? filterTabs(baseTabs, ['DIVISION'])
        : baseTabs

    return Object.keys(tabsToShow).length > 0
      ? tabsToShow
      : DIVISION_VIEW_PLAN_TABS
  }

  // Default case for other users
  const baseTabs =
    isCorporateViewUser || isDivisionViewUser
      ? filterTabs(PLANS_TABS, ['DRAFTS'])
      : { ...PLANS_TABS }

  const tabsWithoutBulkExports = !userUuid
    ? filterTabs(baseTabs, ['BULK_EXPORTS'])
    : baseTabs

  const tabsToShow =
    isCorporateViewUser || isCorporateCreateUser
      ? filterTabs(tabsWithoutBulkExports, ['DIVISION'])
      : tabsWithoutBulkExports

  return Object.keys(tabsToShow).length > 0 ? tabsToShow : PLANS_TABS
}

export function generateBoxSx(
  closestSearchEnabled?: boolean,
  isCorporateViewUser?: boolean,
  isDivisionViewUser?: boolean
) {
  const isViewUser = isCorporateViewUser || isDivisionViewUser
  const searchAndCompareMode = checkSearchAndCompareMode()

  const showSearchAndCompare =
    isViewOnlyEnabled && (isViewUser || searchAndCompareMode)

  return {
    flexGrow: 1,
    overflow: 'auto',
    px: (theme: { spacing: (arg0: number) => string }) =>
      theme.spacing(showSearchAndCompare ? 2 : 0),
    width: '100vw',
    ...(closestSearchEnabled
      ? { height: '100vh' }
      : {
          height: 'calc(100vh - 64px)',
          mt: '64px',
          position: 'relative',
        }),
  }
}

export function generatePlanContainer(
  title: string,
  closestSearchEnabled?: boolean,
  isCorporateViewUser?: boolean,
  isDivisionViewUser?: boolean,
  isFavoritesPage: boolean = false,
  isPendingPlansPage: boolean = false
) {
  const isViewUser = isCorporateViewUser || isDivisionViewUser
  const searchAndCompareMode = checkSearchAndCompareMode()

  const showSearchAndCompare =
    isViewOnlyEnabled && (isViewUser || searchAndCompareMode)

  return (
    <Container
      maxWidth={false}
      disableGutters
      sx={{
        backgroundColor: '#f5f5f5',

        ...(closestSearchEnabled
          ? { mb: 0, ml: 0, mr: 0, mt: 8 }
          : {
              alignItems: 'center',
              display: 'flex',
              height: '64px',
              position: 'sticky',
              top: 0,
              zIndex: 100,
            }),
      }}
    >
      <Grid container spacing={3}>
        <Grid item xs={12} md={12} lg={12}>
          <Toolbar
            sx={{
              pl: 0,
            }}
          >
            <Typography
              sx={{
                flexGrow: 1,
                fontFamily: `'Karla', sans-serif`,
                fontSize: 22,
              }}
            >
              {`${title} ${
                showSearchAndCompare && !isFavoritesPage && !isPendingPlansPage
                  ? PLAN_CONTAINER_LABEL
                  : ''
              }`}
            </Typography>
          </Toolbar>
        </Grid>
      </Grid>
    </Container>
  )
}

export function generateTabsAndTabsBox(
  tabValue: string,
  userUuid: string | null,
  planFamilyType: string | null,
  dispatch: AppDispatch,
  navigate: (path: string) => void,
  handleExport?: () => void,
  closestSearchEnabled?: boolean,
  tabsToShow?: Record<string, string | undefined>,
  isCorporateViewUser: boolean = false,
  isDivisionViewUser: boolean = false,
  isDivisionCreateUser: boolean = false,
  isFavoritesPage: boolean = false
) {
  const searchAndCompareMode = checkSearchAndCompareMode()

  return (
    <Box
      sx={{
        borderBottom: 1,
        borderColor: 'divider',
        mb: 2,
        ...(closestSearchEnabled
          ? {}
          : {
              backgroundColor: 'white',
              height: '48px',
              position: 'sticky',
              top: '64px',
              zIndex: 99,
            }),
      }}
    >
      {tabsToShow && (
        <>
          <Tabs
            value={Object.keys(tabsToShow).indexOf(tabValue)}
            onChange={(event, newValue) =>
              handleTabChange(
                event,
                newValue,
                tabsToShow,
                dispatch,
                navigate,
                userUuid,
                planFamilyType,
                isCorporateViewUser,
                isDivisionViewUser,
                isFavoritesPage
              )
            }
            aria-label="plans tabs"
          >
            {Object.keys(tabsToShow).includes('ALL_PLANS') && (
              <StyledTabTitle title="All Plans">
                <StyledTab
                  label="All Plans"
                  key="All Plans"
                  {...a11yProps(0)}
                />
              </StyledTabTitle>
            )}
            {Object.keys(tabsToShow).includes('SINGLE_FAMILY_PLANS') && (
              <StyledTabTitle title="Single-Family Plans">
                <StyledTab
                  label="Single-Family Plans"
                  key="Single-Family Plans"
                  {...a11yProps(0)}
                />
              </StyledTabTitle>
            )}
            {Object.keys(tabsToShow).includes('SHELL_PLANS') && (
              <StyledTabTitle title="Shell Plans">
                <StyledTab
                  label="Shell Plans"
                  key="Shell Plans"
                  {...a11yProps(5)}
                />
              </StyledTabTitle>
            )}
            {Object.keys(tabsToShow).includes('UNIT_PLANS') && (
              <StyledTabTitle title="Unit Plans">
                <StyledTab
                  label="Unit Plans"
                  key="Unit Plans"
                  {...a11yProps(4)}
                />
              </StyledTabTitle>
            )}
            {Object.keys(tabsToShow).includes('FAVORITES') && (
              <StyledTabTitle title="Favorites">
                <StyledTab
                  label="Favorites"
                  key="Favorites"
                  {...a11yProps(3)}
                />
              </StyledTabTitle>
            )}
            {Object.keys(tabsToShow).includes('SHARED_WITH_ME') && (
              <StyledTabTitle title="Shared with Me">
                <StyledTab
                  label="Shared With Me"
                  key="Shared With Me"
                  {...a11yProps(3)}
                />
              </StyledTabTitle>
            )}
            {((isViewOnlyEnabled &&
              isDivisionCreateUser &&
              !isDivisionViewUser) ||
              (!isViewOnlyEnabled &&
                (isDivisionCreateUser || isDivisionViewUser))) &&
              Object.keys(tabsToShow).includes('DIVISION') && (
                <StyledTabTitle title="Division">
                  <StyledTab
                    label="Division"
                    key="Division"
                    {...a11yProps(1)}
                  />
                </StyledTabTitle>
              )}
            {Object.keys(tabsToShow).includes('DRAFTS') && (
              <StyledTabTitle title="Drafts">
                <StyledTab label="Drafts" key="Drafts" {...a11yProps(2)} />
              </StyledTabTitle>
            )}
            {(searchAndCompareMode ||
              isDivisionViewUser ||
              isCorporateViewUser) && (
              <Tooltip title="Bulk Export to Excel">
                <IconButton
                  sx={{
                    '&:hover': { backgroundColor: 'transparent' },
                    color: 'secondary.main',
                    marginLeft: 'auto',
                  }}
                  onClick={handleExport}
                >
                  <IosShareIcon />
                </IconButton>
              </Tooltip>
            )}
          </Tabs>
        </>
      )}
    </Box>
  )
}

// Note: this function is used to check if corporate create users or division create users have
// manually set search and compare mode by profile settings avatar
export const checkSearchAndCompareMode = () => {
  return (
    localStorage.getItem(SearchAndCompareEnum.KEY) ===
    SearchAndCompareEnum.ENABLED
  )
}

function a11yProps(index: number) {
  return {
    'aria-controls': `simple-tabpanel-${index}`,
    id: `simple-tab-${index}`,
  }
}

export const generateNumericMenuItems = (values: number[]): JSX.Element[] => {
  return values.map((value) => (
    <MenuItem key={value} value={value}>
      {value}
    </MenuItem>
  ))
}

// Type guard to check if the item is an IPlan
export const isPlan = (item: IPlan | IUnit | IShell): item is IPlan => {
  return 'planCategory' in item
}

// Type guard to check if the item is an IUnit
export const isUnit = (item: IPlan | IUnit | IShell): item is IUnit => {
  return 'unitCategory' in item
}

// Type guard to check if the item is an IUnit
export const isShell = (item: IPlan | IUnit | IShell): item is IShell => {
  return 'shellCategory' in item
}

export const isCommunityPlan = (elevationData: IPlan | IUnit | IShell) => {
  return (
    (isPlan(elevationData) &&
      elevationData.planCategory.toLowerCase() === planCategories.COMMUNITY) ||
    (isUnit(elevationData) &&
      elevationData.unitCategory === planCategories.COMMUNITY) ||
    (isShell(elevationData) &&
      elevationData.shellCategory === planCategories.COMMUNITY)
  )
}

export const isCorePlan = (elevationData: IPlan | IUnit | IShell) => {
  return (
    (isPlan(elevationData) &&
      elevationData.planCategory.toLowerCase() === planCategories.CORE) ||
    (isUnit(elevationData) &&
      elevationData.unitCategory === planCategories.CORE) ||
    (isShell(elevationData) &&
      elevationData.shellCategory === planCategories.CORE)
  )
}

// Helper function to check if shell plan is carport or garage
export const isCarportGarageShell = (item: IPlan | IUnit | IShell): boolean => {
  if (!isShell(item)) return false
  else
    return (
      item.structureType === structureTypes.CARPORT ||
      item.structureType === structureTypes.GARAGE
    )
}

// Helper function to get category from either IPlan or IUnit
export const getCategory = (item: IPlan | IUnit | IShell): string => {
  if (isPlan(item)) {
    return item.planCategory?.toLowerCase() ?? ''
  } else if (isUnit(item)) {
    return item.unitCategory ?? ''
  }
  return item.shellCategory ?? ''
}

/**
 * An Approved Regional Plan is consolidated to one document per (plan, business
 * region), so its elevation and divisions arrive as ARRAYS. The DTOs still declare
 * them as scalar strings, so nothing warns when one flows through as an array — it
 * surfaces as `.toUpperCase is not a function` at whichever caller touches it first.
 *
 * The FE's pinned plan-library-common 1.2.10 predates the shared joinValues helper,
 * so these mirror it: blanks dropped, ', ' separated, same output the services
 * already produce for these fields.
 */
export const allMultiValued = (value: unknown): string[] =>
  (Array.isArray(value) ? value : [value]).filter(
    (entry) => entry !== undefined && entry !== null && entry !== ''
  ) as string[]

export const joinMultiValued = (value: unknown): string =>
  allMultiValued(value).join(', ')

// The inverse, splitMultiValued, lives in src/utils/conversion.util so that
// src/lib/utilities can share it without importing this module back.

// Helper function to get elevation from either IPlan or IUnit
export const getElevation = (item: IPlan | IUnit | IShell): string => {
  if (isPlan(item)) {
    return joinMultiValued(item.planElevation)
  } else if (isUnit(item)) {
    return joinMultiValued(item.unitElevation)
  }
  return joinMultiValued(item.shellElevation)
}

/**
 * How many elevations a plan actually covers. Every category but one holds a single
 * elevation per document and returns 1; a consolidated Approved Regional Plan holds
 * all of them in one document, so its comma-separated values are counted. Returns 0
 * when a plan carries no elevation at all, which some Approved Regional Plans do.
 */
export const countElevations = (item: IPlan | IUnit | IShell): number =>
  allMultiValued(
    getElevation(item)
      .split(',')
      .map((value) => value.trim())
  ).length

export const getDisplayCategory = (
  currentElevation: IPlan | IUnit | IShell,
  regions: Record<string, string>,
  divisions: Record<string, string>
) => {
  if (!currentElevation) return ''
  const category = getCategory(currentElevation)

  if (category === planCategories.CORE) {
    return regions[currentElevation.region || ''] ?? currentElevation.region
  } else if (category === planCategories.COMMUNITY) {
    return `${currentElevation.communityId}`
  } else if (
    category === planCategories.HIGH_VALUE_PLAN ||
    category === planCategories.APPROVED_REGIONAL_PLAN
  ) {
    // A consolidated Approved Regional Plan covers several divisions, so every code
    // is resolved and joined rather than assumed scalar — `division?.toUpperCase()`
    // throws on the array these plans carry.
    const codes = allMultiValued(currentElevation.division)
    return (
      codes.map((code) => divisions[code.toUpperCase()] ?? code).join(', ') ||
      joinMultiValued(currentElevation.division)
    )
  }
}

// Helper function to get community id from either IPlan or IUnit
export const getCommunityId = (item: IPlan | IUnit | IShell): string => {
  if (isPlan(item)) {
    return item.communityId
  } else if (isUnit(item)) {
    return item.communityId
  }
  // Note: submitting a core shell plan sends community id as empty string. Also, all shell plans
  // in prod database are community shells. Using null fallback value operator for now
  return item.communityId ?? ''
}

// Helper function to get plan number from either IPlan or IUnit
export const getPlanNumber = (item: IPlan | IUnit | IShell): string => {
  if (isPlan(item)) {
    return item.plan
  } else if (isUnit(item)) {
    return item.plan
  }
  return item.plan
}

// Helper function to get conditioned space square footage
export const getConditionedSqft = (item: IPlan | IUnit): number => {
  if (isPlan(item)) {
    return item.sqftOfHomeConditionedSpace
  }
  return item.sqftOfUnitConditionedSpace
}

// Helper function to get total home square footage
export const getHomeSqft = (item: IPlan | IUnit): number => {
  if (isPlan(item)) {
    return item.homeSqft
  }
  return item.unitSqft || 0
}

// Helper function to get base house price
export const getBasePrice = (item: IPlan | IUnit): number => {
  if (isPlan(item)) {
    return item?.baseHomePrice || 0
  }
  return item?.baseUnitPrice
}

// Helper function to get width in feet
export const getWidthFeet = (item: IPlan | IUnit | IShell): number => {
  if (isPlan(item)) {
    return item.widthOfThePlanInFeet
  } else if (isUnit(item)) {
    return item.unitWidthFeet
  }
  return item.buildingWidthFeet
}

// Helper function to get width in inches
export const getWidthInch = (item: IPlan | IUnit | IShell): number => {
  if (isPlan(item)) {
    return item.widthOfThePlanInInch
  } else if (isUnit(item)) {
    return item.unitWidthInch
  }
  return item?.buildingWidthInch || 0
}

// Helper function to get depth in feet
export const getDepthFeet = (item: IPlan | IUnit | IShell): number => {
  if (isPlan(item)) {
    return item.depthOfThePlanInFeet
  } else if (isUnit(item)) {
    return item.unitDepthFeet
  }
  return item.buildingDepthFeet
}

// Helper function to get depth in inches
export const getDepthInch = (item: IPlan | IUnit | IShell): number => {
  if (isPlan(item)) {
    return item.depthOfThePlanInInch
  } else if (isUnit(item)) {
    return item.unitDepthInch
  }
  return item?.buildingDepthInch || 0
}

// Helper function for determing image type of a core image
export const getImageType = (
  fileTab: string,
  isReversed?: boolean
): ImageTypeEnum => {
  if (fileTab === CorePlanImageTabType.RENDERINGS) {
    return ImageTypeEnum.ELEVATION
  } else if (fileTab === CorePlanImageTabType.FLOOR_PLANS) {
    return isReversed === true
      ? ImageTypeEnum.FLOORPLAN_REVERSED
      : ImageTypeEnum.FLOORPLAN_DEFAULT
  }
  return ImageTypeEnum.OTHER
}

export const getDivision = (
  item: IPlan | IUnit | IShell,
  divisions: Record<string, string>
): string => {
  // Resolves every division of a consolidated Approved Regional Plan rather than
  // assuming one: `division?.toUpperCase()` throws on the array those plans carry.
  const codes = allMultiValued(item.division)
  return (
    codes.map((code) => divisions[code.toUpperCase()] ?? code).join(', ') ||
    joinMultiValued(item.division)
  )
}

// Helper function to extract region from plan category.
// Note: this region output is used for fetching core plans,
// Note: in the locations that this function is used,
//  single family plan region values are coming in differently
//  compared to multify family plan region values
export const getRegionForCorePlan = (
  elevationData: IPlan | IUnit | IShell,
  planClassType: classTypes,
  regionsWithLabels:
    | {
        label: string
        value: string
      }[]
    | undefined
) => {
  const { region } = elevationData

  const regionMapping = regionsWithLabels?.find((mapping) => {
    return mapping.label === region
  })

  const regionValue =
    planClassType === classTypes.SINGLE
      ? regionMapping?.value ?? ''
      : region ?? ''

  return regionValue
}

export const buildPlanKey = (
  planId: string,
  elevationData: IPlan | IUnit | IShell
): string => {
  return isCommunityPlan(elevationData)
    ? `${elevationData.communityId?.slice(
        0,
        -6
      )}_${elevationData.plan.toUpperCase()}`
    : planId.toLowerCase()
}

export const getFilteredFields = ({
  classType,
  excludeRequired = false,
  fields,
  selectedFormat = PDF_EXPORT,
}: FilterFieldsOptions): (FilterMenuItems | ComparisonAttributeItem)[] => {
  const regularFields: (FilterMenuItems | ComparisonAttributeItem)[] = []
  let exportFloorPlansField:
    | FilterMenuItems
    | ComparisonAttributeItem
    | undefined

  fields.forEach((field) => {
    const matchesClassType = field.appliedTo.includes(classType)
    const shouldInclude = excludeRequired
      ? !('isRequired' in field && field.isRequired)
      : true

    if (!matchesClassType || !shouldInclude) {
      return
    }

    if (field.id === fieldNames.EXPORT_FLOOR_PLANS) {
      // We only support exporting floor plans when PDF is selected as export format
      if (selectedFormat === PDF_EXPORT) {
        exportFloorPlansField = field
      }
    } else {
      regularFields.push(field)
    }
  })

  regularFields.sort((a, b) => a.displayName.localeCompare(b.displayName))

  return exportFloorPlansField
    ? [...regularFields, exportFloorPlansField]
    : regularFields
}

// function for adding region key to map when storing core image in viewOnlyImageSlice
export const formatRegion = (
  elevation: IPlan | IUnit | IShell | undefined,
  regionsWithLabels: { label: string; value: string }[] | undefined
) => {
  let region = elevation?.region ?? ''

  // if elevation is shell or unit, change 'gro' and 'par' to 'Grove' and 'Parker'
  if (elevation && !isPlan(elevation)) {
    const regionMapping = regionsWithLabels?.find((mapping) => {
      return mapping.value === region
    })
    region = regionMapping?.label ?? ''
  }

  return region
}

export const getCommunityFloorPlanImages = ({
  communityImages,
  floorNumber,
  includeOptions = false,
}: GetCommunityFloorPlanImagesOptions): CommunityFloorPlanImagesResponse => {
  if (!communityImages) {
    return {
      defaultPlanName: '',
      defaultUrl: '',
      reversedPlanName: '',
      reversedUrl: '',
      ...(includeOptions && { options: [] }),
    }
  }

  const defaultImage = communityImages.find(
    (image) =>
      image.type === ImageTypeEnum.FLOORPLAN_DEFAULT &&
      image.floorplanName === floorNumber
  )

  const reversedImage = communityImages.find(
    (image) =>
      image.type === ImageTypeEnum.FLOORPLAN_REVERSED &&
      image.floorplanName === floorNumber
  )

  const result: CommunityFloorPlanImagesResponse = {
    defaultPlanName: defaultImage?.alt ?? '',
    defaultUrl: defaultImage?.url ?? '',
    reversedPlanName: reversedImage?.alt ?? '',
    reversedUrl: reversedImage?.url ?? '',
  }

  if (includeOptions) {
    const optionImages = communityImages
      .filter(
        (image) =>
          image.type === ImageTypeEnum.FLOORPLAN_OPTION &&
          image.floorplanName === floorNumber
      )
      .map((image) => ({
        title: image.title || 'Option',
        url: image.url,
      }))

    result.options = optionImages
  }

  return result
}

export const hasValidFilterValue = (filterValue: FilterValue): boolean => {
  const { secondValue, value, values, valuesToSearch } = filterValue

  // For range filters (both value and secondValue present)
  if (value !== undefined && secondValue !== undefined) {
    // Both values must be valid for range filters
    const isValueValid =
      value !== null &&
      value !== '' &&
      (typeof value !== 'string' || value.trim() !== '') &&
      (typeof value !== 'number' || (!isNaN(value) && value !== null))

    const isSecondValueValid =
      secondValue !== null &&
      secondValue !== '' &&
      (typeof secondValue !== 'string' || secondValue.trim() !== '') &&
      (typeof secondValue !== 'number' ||
        (!isNaN(secondValue) && secondValue !== null))

    return isValueValid && isSecondValueValid
  }

  // For single value filters
  if (value !== undefined) {
    // For strings, check if not empty
    if (typeof value === 'string' && value.trim() === '') {
      return false
    }
    // For numbers, check if valid number
    if (typeof value === 'number' && (isNaN(value) || value === null)) {
      return false
    }
    // For other types, value exists and is meaningful
    if (value !== null && value !== '') {
      return true
    }
  }

  if (values !== undefined) {
    // Check if array exists and has meaningful values
    return (
      Array.isArray(values) &&
      values.length > 0 &&
      values.some((v) => v !== null && v !== '' && String(v).trim() !== '')
    )
  }

  if (valuesToSearch !== undefined) {
    // Check if array exists and has meaningful values
    return (
      Array.isArray(valuesToSearch) &&
      valuesToSearch.length > 0 &&
      valuesToSearch.some(
        (v) => v !== null && v !== '' && String(v).trim() !== ''
      )
    )
  }

  return false
}

const valueToLabelMap: Record<string, Record<string, string>> = {
  [fieldNames.FOUNDATION_TYPE]: foundationType_,
  [fieldNames.PLAN_STATUS]: planStatus_,
  [fieldNames.PLAN_TYPE]: planType_,
  [fieldNames.STRUCTURE_TYPE]: structureType,
  [shellFieldNames.SHELL_STATUS]: planStatus_,
  [shellFieldNames.SHELL_TYPE]: planType_,
  [unitFieldNames.UNIT_TYPE]: planType_,
}

export const squareFeetFields: string[] = [
  fieldNames.SQFT_HOME,
  fieldNames.SQFT_OF_HOME_CONDITIONED_SPACE,
  unitFieldNames.SQFT_OF_UNIT_CONDITIONED_SPACE,
  unitFieldNames.SQFT_UNIT,
  shellFieldNames.BUILDING_UNDER_ROOF_SQ_FT,
  shellFieldNames.BUILDING_SQ_FT,
]

const elevationFields: string[] = [
  fieldNames.PLAN_ELEVATION,
  unitFieldNames.UNIT_ELEVATION,
  shellFieldNames.SHELL_ELEVATION,
]

export const priceFields: string[] = [
  fieldNames.BASE_HOUSE_PRICE,
  fieldNames.BASE_HOUSE_PRE,
  unitFieldNames.BASE_UNIT_PRICE,
  unitFieldNames.BASE_UNIT_PRE,
]

export const dateFields: string[] = [
  fieldNames.PLAN_CREATED_DATE,
  fieldNames.PLAN_MODIFIED_DATE,
]

export const ADDITIONAL_ROOM_LABELS_BY_ID: Record<string, string> = {
  additionalBathrooms: 'Additional Bathrooms',
  bonusRoom: 'Bonus Room',
  familyRoom: 'Family Room',
  flex: 'Flex',
  gameRoom: 'Game Room',
  greatRoom: 'Great Room',
  loft: 'Loft',
  study: 'Study',
  sunRoom: 'Sun Room',
}

export const isAdditionalRoomPresent = (
  roomId: string,
  additionalRooms: unknown
): boolean => {
  const label = ADDITIONAL_ROOM_LABELS_BY_ID[roomId]
  if (!label || typeof additionalRooms !== 'string') return false
  return additionalRooms
    .split(',')
    .map((room) => room.trim())
    .includes(label)
}

export const formatExportValue = (
  value: unknown,
  propertyName: string,
  divisions: Record<string, string>,
  regions: Record<string, string>
): string => {
  if (value === undefined || value === null) return ''
  const stringValue = String(value)

  if (Array.isArray(value)) return value.join(', ')

  if (squareFeetFields.includes(propertyName)) return getFeet(value as number)

  if (priceFields.includes(propertyName)) return formatCurrency(Number(value))

  if (elevationFields.includes(propertyName)) return stringValue?.toUpperCase()

  if (stringValue in yesNo) return yesNo[value as keyof typeof yesNo]

  const date = new Date(value as Date)
  if (!isNaN(date.getTime()) && value === date.toISOString()) {
    return toDateString(value)
  }

  if (propertyName === fieldNames.DIVISION) {
    return (divisions[value as keyof typeof divisions] || value) as string
  }

  if (propertyName === fieldNames.REGION) {
    return (regions[value as keyof typeof regions] || value) as string
  }

  if (
    Object.keys(valueToLabelMap).includes(propertyName as string) &&
    (value as string) in valueToLabelMap[propertyName]
  ) {
    return valueToLabelMap[propertyName][stringValue]
  }

  return stringValue
}

interface BulkExportPayload {
  fileId: string
  fileName: string
  fileUrl: string
}

export const isBulkExportPayload = (
  payload: unknown
): payload is BulkExportPayload => {
  if (typeof payload !== 'object' || payload === null) {
    return false
  }

  const obj = payload as Record<string, unknown>
  return (
    typeof obj.fileId === 'string' &&
    typeof obj.fileName === 'string' &&
    typeof obj.fileUrl === 'string'
  )
}

export const handleHelpMe = () => {
  const mailtoLink = `mailto:${HELP_ME_RECIPIENT}?subject=${encodeURIComponent(
    HELP_ME_SUBJECT
  )}&body=${encodeURIComponent(HELP_ME_BODY)}`

  const anchor = document.createElement('a')
  anchor.href = mailtoLink
  document.body.appendChild(anchor)
  anchor.click()
  document.body.removeChild(anchor)
}
